import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { SUPPORTED_APPS } from '../constants';
import { AppData, SystemLog } from '../types';
import { Shield, Smartphone, AlertTriangle, ArrowLeft, CheckCircle, Terminal, Activity, Server, Clock, Users } from 'lucide-react';
import SEO from '../components/SEO';

const AppDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [app, setApp] = useState<AppData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'status'>('overview');

  useEffect(() => {
    // Simulate data fetch
    const foundApp = SUPPORTED_APPS.find((a) => a.slug === slug);
    setApp(foundApp || null);
    setLoading(false);
  }, [slug]);

  if (loading) return <div className="min-h-screen pt-24 text-center text-slate-900">Loading...</div>;

  if (!app) {
    return (
      <div className="min-h-screen pt-24 text-center px-4">
        <SEO title="Aplikasi Tidak Ditemukan" description="Halaman tidak ditemukan." />
        <h1 className="text-3xl font-bold text-slate-900 mb-4">Aplikasi Tidak Ditemukan</h1>
        <p className="text-slate-600 mb-8">Maaf, kami belum memiliki data untuk aplikasi ini.</p>
        <Link to="/aplikasi-didukung" className="text-brand-primary hover:underline">Kembali ke Daftar Aplikasi</Link>
      </div>
    );
  }

  // --- Dynamic SEO Construction ---
  
  // Keyword-rich title strategy: "Action + App Name + Key Feature"
  const seoTitle = `Solusi GPS ${app.name} Tanpa Root - Optimasi Lokasi Presisi`;
  
  // Meta description targeting pain points
  const seoDesc = `Cara aman mengatasi GPS error pada ${app.name} (${app.version}). Atasi masalah sinyal lemah, radius check error, dan validasi lokasi pada ${app.name} tanpa root HP. Status Server: ${app.status === 'active' ? 'Online' : 'Maintenance'}.`;

  // JSON-LD Structured Data (SoftwareApplication)
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": `XCape Optimizer for ${app.name}`,
    "operatingSystem": "Android",
    "applicationCategory": "UtilitiesApplication",
    "description": app.description,
    "softwareVersion": app.version,
    "offers": {
      "@type": "Offer",
      "price": "100000",
      "priceCurrency": "IDR",
      "availability": "https://schema.org/InStock"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": app.uptimePercentage.replace('%', '') > "95" ? "4.8" : "4.2", // Dynamic rating based on uptime mock
      "ratingCount": "1250"
    }
  };

  // Quota Logic
  const quotaPercentage = Math.min(100, Math.round((app.quotaUsed / app.quotaTotal) * 100));
  const isFull = app.quotaUsed >= app.quotaTotal;
  const slotsLeft = app.quotaTotal - app.quotaUsed;
  
  let quotaColor = 'bg-green-500';
  let quotaText = 'text-green-600';
  
  if (quotaPercentage > 75) {
    quotaColor = 'bg-orange-500';
    quotaText = 'text-orange-600';
  }
  if (quotaPercentage > 95) {
    quotaColor = 'bg-red-500';
    quotaText = 'text-red-600';
  }

  return (
    <div className="pt-24 pb-16">
      <SEO 
        title={seoTitle}
        description={seoDesc}
        schema={structuredData}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <Link to="/aplikasi-didukung" className="inline-flex items-center text-slate-500 hover:text-brand-primary mb-8 transition-colors" aria-label="Kembali ke daftar aplikasi">
          <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" /> Kembali ke Daftar
        </Link>

        {/* Header App Card */}
        <div className="bg-white border border-slate-200 rounded-3xl p-8 mb-8 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-slate-50 rounded-full -translate-y-1/2 translate-x-1/2 z-0"></div>
          <div className="relative z-10 flex flex-col md:flex-row md:items-start justify-between gap-6">
            <div className="flex items-center gap-6">
              <div className="h-20 w-20 bg-gradient-to-br from-brand-primary to-orange-600 rounded-2xl flex items-center justify-center text-3xl font-bold text-white shadow-lg shadow-orange-500/20 flex-shrink-0">
                {app.name.charAt(0)}
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900 mb-2">{app.name}</h1>
                <div className="flex flex-wrap gap-2 text-sm mb-4">
                  <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full border border-slate-200 font-mono text-xs flex items-center gap-1">
                    <Terminal className="h-3 w-3" aria-hidden="true" /> {app.version}
                  </span>
                  <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full border border-slate-200 text-xs flex items-center gap-1">
                    <Clock className="h-3 w-3" aria-hidden="true" /> Updated: {app.lastTested}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col items-end gap-3 w-full md:w-auto">
              <div className={`px-4 py-2 rounded-full border flex items-center gap-2 text-sm ${
                app.status === 'active' 
                  ? 'bg-green-50 border-green-200 text-green-700'
                  : 'bg-yellow-50 border-yellow-200 text-yellow-700'
              }`}>
                {app.status === 'active' ? <CheckCircle className="h-4 w-4" aria-hidden="true" /> : <AlertTriangle className="h-4 w-4" aria-hidden="true" />}
                <span className="font-bold">
                  {app.status === 'active' ? 'SYSTEM ONLINE' : 'MAINTENANCE MODE'}
                </span>
              </div>
              
              {/* Quota Indicator */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 w-full md:w-72 mt-2 shadow-sm">
                 <div className="flex justify-between items-center text-xs mb-2">
                    <span className="font-semibold text-slate-600 flex items-center gap-1">
                        <Users className="h-3 w-3" /> Server Capacity
                    </span>
                    <span className={`font-mono font-bold ${quotaText}`}>
                        {Math.floor(quotaPercentage)}% Used
                    </span>
                 </div>
                 <div className="h-3 bg-slate-200 rounded-full overflow-hidden border border-slate-300/50 mb-2">
                    <div 
                        className={`h-full rounded-full transition-all duration-500 ${quotaColor}`} 
                        style={{ width: `${quotaPercentage}%` }}
                    ></div>
                 </div>
                 <div className="flex justify-between items-end">
                     <div className="text-[10px] text-slate-400">
                         Total: {new Intl.NumberFormat('id-ID').format(app.quotaTotal)}
                     </div>
                     <div className={`text-xs font-bold ${isFull ? 'text-red-600' : 'text-slate-700'}`}>
                        {isFull ? 'SERVER FULL' : `Tersedia: ${new Intl.NumberFormat('id-ID').format(slotsLeft)} Slot`}
                     </div>
                 </div>
              </div>

            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="flex space-x-1 bg-slate-100/50 p-1 rounded-xl mb-8 w-fit border border-slate-200" role="tablist">
          <button
            role="tab"
            aria-selected={activeTab === 'overview'}
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'overview'
                ? 'bg-white text-brand-primary shadow-sm ring-1 ring-slate-200'
                : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
            }`}
          >
            Overview
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'status'}
            onClick={() => setActiveTab('status')}
            className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'status'
                ? 'bg-white text-brand-primary shadow-sm ring-1 ring-slate-200'
                : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
            }`}
          >
            System Status & Logs
          </button>
        </div>

        {activeTab === 'overview' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12 animate-fade-in-up">
              {/* Features & Guide */}
              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                  {/* Changed h3 to h2 to maintain hierarchy under h1 */}
                  <h2 className="text-lg font-bold text-slate-900 mb-6 flex items-center">
                      <Shield className="h-5 w-5 mr-2 text-brand-primary" aria-hidden="true" /> Signal Optimization Features
                  </h2>
                  <ul className="space-y-4">
                      {app.features.map((feat, i) => (
                          <li key={i} className="flex items-start">
                          <div className="h-6 w-6 rounded-full bg-orange-100 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                            <CheckCircle className="h-4 w-4 text-brand-primary" aria-hidden="true" />
                          </div>
                          <span className="text-slate-700 text-sm font-medium pt-1">{feat}</span>
                        </li>
                      ))}
                  </ul>
              </div>

              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                  {/* Changed h3 to h2 */}
                  <h2 className="text-lg font-bold text-slate-900 mb-6 flex items-center">
                      <Smartphone className="h-5 w-5 mr-2 text-blue-500" aria-hidden="true" /> Panduan Penggunaan
                  </h2>
                  <div className="relative pl-4 border-l-2 border-slate-100 space-y-6">
                    {[
                      "Buka XCape Tuner dan pilih profil kalibrasi ini.",
                      "Aktifkan 'Precision Lock' dan tunggu indikator hijau.",
                      `Buka aplikasi ${app.name} dan lakukan check-in dengan sinyal yang sudah stabil.`,
                      "Jangan matikan tuner sampai proses upload data selesai."
                    ].map((step, idx) => (
                      <div key={idx} className="relative">
                        <span className="absolute -left-[21px] top-0 h-3 w-3 rounded-full bg-slate-200 border-2 border-white"></span>
                        <p className="text-sm text-slate-600">{step}</p>
                      </div>
                    ))}
                  </div>
              </div>

              {/* Technical Deep Dive */}
              <div className="md:col-span-2 prose prose-slate max-w-none bg-slate-50 rounded-2xl p-8 border border-slate-100">
                  {/* Changed h3 to h2 */}
                  <h2 className="text-slate-900 font-bold mb-2">Technical Analysis</h2>
                  <p className="text-slate-600 mb-4">
                      {app.name} menggunakan metode deteksi lokasi yang sensitif terhadap noise. XCape mengatasinya dengan <strong>Signal Filtering</strong> yang membersihkan data GPS sebelum mencapai aplikasi.
                  </p>
                  <p className="text-slate-600 text-sm">
                    Engine kami mensimulasikan data akselerometer dan giroskop untuk memvalidasi bahwa perangkat "sedang dipegang" manusia, memastikan integritas data sensor sesuai dengan kondisi lapangan.
                  </p>
              </div>
          </div>
        ) : (
          <div className="space-y-6 animate-fade-in-up">
            {/* Uptime Dashboard */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-bold text-slate-900 flex items-center gap-2">
                    <Activity className="h-5 w-5 text-green-500" aria-hidden="true" /> Uptime History (30 Days)
                  </h2>
                  <span className="text-2xl font-bold text-slate-900">{app.uptimePercentage}</span>
                </div>
                <div className="flex items-end gap-1 h-24">
                  {app.uptimeHistory.map((day, idx) => (
                    <div 
                      key={idx}
                      className={`flex-1 rounded-sm transition-all hover:opacity-80 group relative ${
                        day.status === 'operational' ? 'bg-green-400' : day.status === 'degraded' ? 'bg-yellow-400' : 'bg-red-500'
                      }`}
                      style={{ height: `${day.percentage}%` }}
                      aria-label={`${day.date}: ${day.status}`}
                    >
                      <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-900 text-white text-xs p-2 rounded whitespace-nowrap z-10">
                        {day.date}: {day.percentage}%
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-2 text-xs text-slate-400">
                  <span>30 days ago</span>
                  <span>Today</span>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm flex flex-col justify-center">
                 <h2 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Server className="h-5 w-5 text-blue-500" aria-hidden="true" /> Server Node
                 </h2>
                 <div className="space-y-4">
                   <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Node Region</span>
                      <span className="font-mono text-slate-900">ID-JKT-04</span>
                   </div>
                   <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Latency</span>
                      <span className="font-mono text-green-600">14ms</span>
                   </div>
                   <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Load</span>
                      <span className="font-mono text-slate-900">32%</span>
                   </div>
                   <div className="pt-4 border-t border-slate-100">
                      <span className="inline-block px-2 py-1 bg-green-100 text-green-700 text-xs rounded font-bold">OPERATIONAL</span>
                   </div>
                 </div>
              </div>
            </div>

            {/* Live Logs Terminal */}
            <div className="bg-slate-900 rounded-2xl p-6 border border-slate-800 shadow-xl overflow-hidden">
               <div className="flex items-center gap-2 mb-4 border-b border-slate-800 pb-2">
                 <Terminal className="h-4 w-4 text-slate-400" aria-hidden="true" />
                 <span className="text-xs font-mono text-slate-400">System Activity Log</span>
               </div>
               <div className="font-mono text-xs space-y-2 h-48 overflow-y-auto custom-scrollbar">
                  {app.recentLogs.length > 0 ? (
                    app.recentLogs.map((log) => (
                      <div key={log.id} className="flex gap-4">
                        <span className="text-slate-500 min-w-[80px]">{log.timestamp}</span>
                        <span className={`
                          ${log.level === 'success' ? 'text-green-400' : ''}
                          ${log.level === 'info' ? 'text-blue-300' : ''}
                          ${log.level === 'warning' ? 'text-yellow-400' : ''}
                          ${log.level === 'error' ? 'text-red-400' : ''}
                        `}>
                          {log.message}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="text-slate-600 italic">No recent logs available...</div>
                  )}
                  <div className="animate-pulse text-brand-primary">_</div>
               </div>
            </div>

            {/* Incidents Section */}
            {app.activeIncidents.length > 0 && (
               <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6">
                  <h3 className="text-lg font-bold text-yellow-800 mb-4 flex items-center">
                    <AlertTriangle className="h-5 w-5 mr-2" aria-hidden="true" /> Recent Incidents
                  </h3>
                  <div className="space-y-4">
                    {app.activeIncidents.map((inc) => (
                      <div key={inc.id} className="bg-white p-4 rounded-xl border border-yellow-100">
                        <div className="flex justify-between mb-2">
                           <span className="font-bold text-slate-800">{inc.title}</span>
                           <span className="text-xs text-slate-500">{inc.date}</span>
                        </div>
                        <p className="text-sm text-slate-600 mb-2">{inc.description}</p>
                        <span className="inline-block px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded font-bold uppercase">
                          {inc.status}
                        </span>
                      </div>
                    ))}
                  </div>
               </div>
            )}
          </div>
        )}

        <div className="mt-12 text-center border-t border-slate-100 pt-12">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Siap Mengamankan Absensi Anda?</h3>
             {isFull ? (
                <button disabled className="inline-block bg-slate-300 text-slate-500 cursor-not-allowed font-bold py-4 px-12 rounded-full shadow-none">
                    Quota Penuh - Masuk Waiting List
                </button>
            ) : (
                <Link to="/harga" className="inline-block bg-brand-primary hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-full shadow-lg shadow-orange-500/30 transition-transform hover:scale-105">
                    Download XCape untuk {app.name}
                </Link>
            )}
        </div>

      </div>
    </div>
  );
};

export default AppDetail;